import { useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '../button/button.component';
import { Drawer } from '../drawer/drawer.component';
import { Icon } from '../icon/icon.component';
import { Row } from '../row/row.component';
import { Spinner } from '../spinner/spinner.component';
import { Tab } from '../tab/tab.component';
import { Text } from '../text/text.component';
import { Zone, ZoneArea } from '../zone/zone.component';
import './filter.component.scss';
import { FilterFieldMetadata, generateFilterPills } from './filter.hook';

export declare namespace FilterType {
  type Props = {
    className?: string | null;
    disabled?: boolean;
    children?: React.ReactNode | Array<React.ReactNode> | null;
    subtitle?: string | null;
    onReset?: () => void;
    onValidate?: () => void;
    onClose?: () => void;
    isLoading?: boolean;
    disableSubmit?: boolean;
  };
}

export const Filter = observer(
  ({
    className,
    disabled,
    children,
    subtitle,
    onReset,
    onValidate,
    onClose,
    isLoading,
    disableSubmit,
  }: FilterType.Props) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { t } = useTranslation();

    const handleOpen = useCallback(() => {
      setIsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    const handleValidateClick = useCallback(() => {
      onValidate?.();
      setIsOpen(false);
    }, [onValidate]);

    return (
      <div className={classNames('filter', className)}>
        <Tab onClick={handleOpen} disabled={disabled} icon="filter">
          {t('watermelon-moreFilters')}
        </Tab>
        <Drawer
          title={t('watermelon-filters')}
          subTitle={subtitle}
          isOpen={isOpen}
          onClose={handleClose}
        >
          {!isLoading ? (
            <Zone
              zones={[['content'], ['actions']]}
              rows={['1fr', 'min-content']}
              columns={['1fr']}
              gapY={24}
            >
              <ZoneArea className="filter__content" area="content">
                {children}
              </ZoneArea>
              <ZoneArea area="actions">
                <Row width="max-content" justifyContent="flex-end" gap={16}>
                  <Button
                    size="large"
                    variant="secondary"
                    minWidth={true}
                    onClick={() => onReset?.()}
                  >
                    {t('watermelon-reset')}
                  </Button>
                  <Button
                    size="large"
                    disabled={disableSubmit}
                    minWidth={true}
                    onClick={handleValidateClick}
                  >
                    {t('watermelon-validate')}
                  </Button>
                </Row>
              </ZoneArea>
            </Zone>
          ) : (
            <Spinner size="medium" />
          )}
        </Drawer>
      </div>
    );
  },
);

export declare namespace FilterPillType {
  type Props = {
    children: React.ReactNode;
    onClose?: () => void;
  };
}

export const FilterPill = observer(
  ({ children, onClose }: FilterPillType.Props) => {
    return (
      <div className="filterPill">
        <Text whiteSpace="nowrap" color="deep_indigo_70" variant="subhead">
          {children}
        </Text>
        <div className="filterPill__close" onClick={onClose}>
          <Icon variant="deleteFields" />
        </div>
      </div>
    );
  },
);

export declare namespace FilterPillGroupType {
  type Props<T extends Record<string, any> = any> = {
    className?: string | null;
    beforeMoreMax?: number;
    afterMoreMax?: number;
    filterRef?: React.RefObject<{
      getMetadata: () => Record<string, FilterFieldMetadata>;
    }>;
    filterValues?: T | null;
    onRemovePill?: (fieldKey: string, value: string) => void;
    children?:
      | React.ReactElement<FilterPillType.Props>
      | React.ReactElement<FilterPillType.Props>[]
      | null;
  };
}

export const FilterPillGroup = observer(
  <T extends Record<string, any>>({
    className,
    children,
    beforeMoreMax = 6,
    afterMoreMax = 20,
    filterRef,
    filterValues,
    onRemovePill,
  }: FilterPillGroupType.Props<T>) => {
    const [displayOther, setDisplayOther] = useState<boolean>(false);
    const { t } = useTranslation();
    const pills = useMemo(() => {
      if (!filterRef || !filterValues || !filterRef.current) return [];

      const metadata = filterRef.current.getMetadata();
      return generateFilterPills(filterValues, metadata);
    }, [filterValues, filterRef]);

    const hasPills = pills.length > 0;
    const hasChildren = React.Children.count(children) > 0;

    const content = useMemo(() => {
      if (hasPills && onRemovePill) {
        return pills.map((pill, index) => (
          <FilterPill
            key={`${pill.fieldKey}-${pill.value}-${index}`}
            onClose={() => onRemovePill(pill.fieldKey, pill.value)}
          >
            {pill.displayValue}
          </FilterPill>
        ));
      }
      return React.Children.toArray(children);
    }, [hasPills, pills, onRemovePill, children]);

    const childrenFormatted = useMemo(() => {
      if (!displayOther) return content.slice(0, beforeMoreMax);
      return content;
    }, [displayOther, beforeMoreMax, content]);

    if (!hasPills && !hasChildren) return null;

    return (
      <div className={classNames('filterPillGroup', className)}>
        {childrenFormatted.slice(0, afterMoreMax)}
        {!displayOther && content.length > beforeMoreMax && (
          <div
            className="filterPillGroup__more"
            onClick={() => setDisplayOther(true)}
          >
            <Text color="white_100" variant="sublabel">
              {t('watermelon-more')}
            </Text>
          </div>
        )}
      </div>
    );
  },
);
